import type { ColumnConfig } from '../hooks/useColumnConfig';
import type { GroupFieldConfig, GroupLevel, GroupState } from '../components/common/GroupPanel/GroupPanel';

/** MDF-39: the most group levels a table nests (for example Coach, then Status). */
export const MAX_GROUP_LEVELS = 2;

/** One group header in a nested grouping. `rows` holds every row under the header. */
export interface GroupNode<T> {
  /** Unique across all levels; collapse state keys on it */
  key: string;
  label: string;
  /** 0 = outermost level */
  depth: number;
  rows: T[];
  children: GroupNode<T>[] | null;
}

/**
 * Converts any stored group value to a GroupState, or null for no grouping.
 * Accepts the old single-object shape `{ field, order }` as a one-level state.
 */
export function normalizeGroupState(value: unknown): GroupState | null {
  const raw = Array.isArray(value) ? value : value ? [value] : [];
  const levels: GroupLevel[] = [];
  for (const item of raw) {
    const field = (item as Partial<GroupLevel> | null)?.field;
    if (typeof field !== 'string' || !field || levels.some(l => l.field === field)) continue;
    const order = (item as Partial<GroupLevel>).order === 'alphabetical' ? 'alphabetical' : 'custom';
    levels.push({ field, order });
  }
  return levels.length > 0 ? levels.slice(0, MAX_GROUP_LEVELS) : null;
}

/**
 * MDF-39: a header that is only a number, Yes/No or "(No value)" names nothing, so it gets its
 * field label ("Session Number: 2"). A value that reads on its own ("Group 1", "Active") stays.
 */
export function groupHeaderLabel(value: string, fieldLabel: string | undefined): string {
  return fieldLabel && /^(-?\d+(\.\d+)?%?|yes|no|true|false|\(no value\))$/i.test(value)
    ? `${fieldLabel}: ${value}`
    : value;
}

/**
 * MDF-39: two cohorts can each have a "Group 1", so a Group level keys on group_id, not the name.
 * When the rows span more than one cohort, the label names the cohort ("KR Test / Group 1").
 * Returns [key, label, rows] in first-seen order, or by label for 'alphabetical'.
 */
function groupByGroupId<T extends Record<string, unknown>>(
  data: T[],
  order: GroupLevel['order'],
  multiCohort: boolean,
): [string, string, T[]][] {
  const groups = new Map<string, [string, T[]]>();
  for (const row of data) {
    const key = row.group_id ? String(row.group_id) : '(No value)';
    if (!groups.has(key)) {
      const name = formatGroupKey(row.group_name);
      groups.set(key, [row.group_id && multiCohort && row.cohort_name ? `${row.cohort_name} / ${name}` : name, []]);
    }
    groups.get(key)![1].push(row);
  }
  const entries = Array.from(groups, ([key, [label, rows]]): [string, string, T[]] => [key, label, rows]);
  if (order === 'alphabetical') {
    entries.sort(([a, la], [b, lb]) => (a === '(No value)' ? 1 : b === '(No value)' ? -1 : la.localeCompare(lb)));
  }
  return entries;
}

/**
 * Groups records by each level in turn: level 1 headers hold level 2 headers, which hold rows.
 * Pass the tab's columns so a bare number or Yes/No header names its field.
 * Returns null when there is no grouping.
 */
export function groupDataByLevels<T extends Record<string, unknown>>(
  data: T[],
  levels: GroupState | null | undefined,
  columns?: Pick<ColumnConfig, 'id' | 'label'>[],
  depth = 0,
  parentKey = '',
  // Set once from all rows, so a group has the same label under every parent header
  multiCohort = new Set(data.map(r => r.cohort_id)).size > 1,
): GroupNode<T>[] | null {
  const [level, ...rest] = levels ?? [];
  if (!level?.field) return null;
  const fieldLabel = columns?.find(c => c.id === level.field)?.label;
  const entries =
    level.field === 'group_name' && data.some(r => r.group_id)
      ? groupByGroupId(data, level.order, multiCohort)
      : (groupDataByField(data, level.field, level.order) ?? []).map(
          ([value, rows]): [string, string, T[]] => [value, value, rows],
        );
  return entries.map(([value, text, rows]) => {
    // The key stays the raw value (the group id for Group), so collapse state does not depend on the label.
    const key = depth === 0 ? value : `${parentKey}\u0000${value}`;
    return {
      key,
      label: groupHeaderLabel(text, fieldLabel),
      depth,
      rows,
      children: rest.length > 0 ? groupDataByLevels(rows, rest, columns, depth + 1, key, multiCohort) : null,
    };
  });
}

/** The header and row items a table renders, in order, skipping everything under a collapsed header. */
export function flattenGroups<T>(
  nodes: GroupNode<T>[],
  collapsed: Set<string>,
): ({ group: GroupNode<T> } | { row: T })[] {
  return nodes.flatMap(group => [
    { group },
    ...(collapsed.has(group.key)
      ? []
      : group.children
        ? flattenGroups(group.children, collapsed)
        : group.rows.map(row => ({ row }))),
  ]);
}

/**
 * Groups an array of records by a given field name.
 * Uses generic property access so every hook doesn't need a per-field switch statement.
 *
 * @param data       The array of records to group
 * @param field      The property key to group by (must exist on the record)
 * @param order      'alphabetical' sorts group keys A→Z; 'custom' preserves insertion order
 * @returns          Array of [groupKey, records[]] tuples, or null if field is falsy
 */
export function groupDataByField<T extends Record<string, unknown>>(
  data: T[],
  field: string | null | undefined,
  order: 'custom' | 'alphabetical' = 'custom',
): [string, T[]][] | null {
  if (!field) return null;

  const groups = new Map<string, T[]>();

  // Survey-question columns use `q_<questionId>` as the column ID,
  // but the actual response value lives in `record.responses[questionId]`.
  const isQuestionField = field.startsWith('q_');
  const questionId = isQuestionField ? field.slice(2) : null;

  for (const record of data) {
    const raw = isQuestionField && questionId
      ? (record as Record<string, unknown> & { responses?: Record<string, unknown> }).responses?.[questionId]
      : record[field];
    const key = formatGroupKey(raw);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(record);
  }

  const entries = Array.from(groups.entries());
  if (order === 'alphabetical') {
    entries.sort(([a], [b]) => {
      // Push "(No value)" to the end
      if (a === '(No value)') return 1;
      if (b === '(No value)') return -1;
      return a.localeCompare(b);
    });
  }
  return entries;
}

/** Pattern matching ISO 8601 date strings (e.g., "2026-02-24T12:00:00.000Z") */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/**
 * Converts a raw field value into a human-readable group key string.
 * Handles null, undefined, empty string, booleans, numbers, dates, ISO strings, and arrays.
 */
function formatGroupKey(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '(No value)';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(String).join(', ') : '(No value)';
  }
  const str = String(value).trim();
  if (str === '') return '(No value)';
  // Format ISO date strings into a readable date (e.g., "Feb 24, 2026")
  if (ISO_DATE_PATTERN.test(str)) {
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
  return str;
}

/**
 * Derives GroupFieldConfig[] from a ColumnConfig[] array.
 * Includes only columns where `groupable` is not explicitly false.
 */
export function deriveGroupFields(columns: ColumnConfig[]): GroupFieldConfig[] {
  return columns
    .filter(col => col.groupable !== false)
    .map(col => ({ value: col.id, label: col.label }));
}
